import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}

export default function Modal({ title, onClose, children, toolbar, footer, wide = false }: ModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className={`relative bg-ui-surface border border-ui-border rounded-t-2xl sm:rounded-lg w-full ${wide ? 'sm:max-w-3xl' : 'sm:max-w-xl'} max-h-[92dvh] sm:max-h-[95dvh] flex flex-col`}>
        <div className="sm:hidden mx-auto mt-3 mb-1 h-1 w-10 rounded-full bg-ui-border shrink-0" />
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-ui-border shrink-0">
          <h2 className="text-base font-semibold text-ui-text">{title}</h2>
          <button onClick={onClose} className="text-ui-text-muted hover:text-ui-text text-lg leading-none">✕</button>
        </div>
        {toolbar && <div className="shrink-0 border-b border-ui-border">{toolbar}</div>}
        <div className={`px-4 sm:px-6 pt-4 ${footer ? '' : 'pb-5'} overflow-y-auto flex-1`}>{children}</div>
        {footer && <div className="shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
